<template>
  <div class="recycler-table">
    <div class="recycler-header recycler-flex">
      <span
        v-for="(header, idx) in modifiedHeaders"
        :key="`${idx}${header.value}`"
        :ref="header.value"
        @click="onClickHandler(header)"
        :style="{ minWidth: `${columnWidthMap[header.value]}px` }"
        :class="idx == 0 ? 'fixed-col cell' : 'cell'"
      >
        <span>
          {{ $t(header.key) }}
          <v-icon
            v-if="header.sortable"
            small
            :color="
              activeSort === header.value
                ? isDark
                  ? '#fff'
                  : '#000'
                : '#60677E'
            "
          >
            {{
              sort[header.value] === "asc" ? "arrow_upward" : "arrow_downward"
            }}
          </v-icon>
        </span>
      </span>
    </div>
    <RecycleScroller
      class="recycler--height"
      :items="tableData"
      :key-field="key_field"
      :item-size="40"
      :buffer="100"
    >
      <template slot="default" slot-scope="{ item, index }">
        <span
          v-for="(header, idx) in modifiedHeaders"
          :key="`${index}${idx}${header.value}`"
          @click="sortStateHandler(header)"
          :class="
            idx == 0
              ? 'fixed-col cell'
              : item[header.value] == null
              ? 'error--text cell'
              : 'number--text cell'
          "
          :style="{ width: `${columnWidthMap[header.value]}px` }"
        >
          {{ item[header.value] != null ? item[header.value] : $t("No Data") }}
        </span>
      </template>
    </RecycleScroller>
  </div>
</template>

<script>
import _ from "lodash";
import { miscellaneous, mapGettersMixin } from "@/util/mixin/index.js";

export default {
  mixins: [miscellaneous, mapGettersMixin],
  props: {
    items: {
      type: Array,
      default: function () {
        return [];
      },
      required: true,
    },
    headers: {
      type: Array,
      default: function () {
        return [];
      },
      required: true,
    },
    key_field: {
      type: String,
      required: true,
    },
    slotWise: {
      type: Boolean,
      default: false,
    },
    reduceHeight: {
      type: Number,
      default: 405
    }
  },
  data() {
    return {
      tableData: [],
      activeSort: "",
      sort: {},
    };
  },
  computed: {
    modifiedHeaders() {
      const headers = this.headers.map((header) => ({
        ...header,
        sortable: true,
      }));
      return headers;
    },
    columnWidthMap() {
      const widthMap = {};
      this.headers.forEach((head) => {
        widthMap[head.value] =
          this.$t(head.key).length * 8 + 10 > 210
            ? this.$t(head.key).length * 8 + 10
            : 210;

        this.items.forEach((data) => {
          if (widthMap[head.value] < String(data[head.value]).length * 8 + 10) {
            widthMap[head.value] = String(data[head.value]).length * 8 + 10;
          }
        });
      });

      return widthMap;
    },
  },
  mounted() {
    this.tableData = _.cloneDeep(this.items);
    this.initialSort();
    if (this.tableData.length == 0) {
      const newItem = {};
      this.modifiedHeaders.forEach((el) => {
        newItem[el.value] = "No Data";
      });
      this.tableData.push(newItem);
    }

    this.$el
      .querySelector(".recycler-header")
      .addEventListener(
        "scroll",
        this.scrollHandler.bind(this, "vue-recycle-scroller")
      );

    this.$el
      .querySelector(".vue-recycle-scroller")
      .addEventListener(
        "scroll",
        this.scrollHandler.bind(this, "recycler-header")
      );

    this.$el.querySelector(".recycler--height").style.height = `calc(100vh - ${this.reduceHeight}px)`;
  },
  destroyed() {
    this.$el
      .querySelector(".recycler-header")
      .removeEventListener(
        "scroll",
        this.scrollHandler.bind(this, "vue-recycle-scroller")
      );
    this.$el
      .querySelector(".vue-recycle-scroller")
      .removeEventListener(
        "scroll",
        this.scrollHandler.bind(this, "recycler-header")
      );
  },
  methods: {
    onClickHandler(header) {
      if (header.sortable) {
        this.sortStateHandler(header.value);
        this.tableData = this.arraySortHandler(
          this.tableData,
          header.value,
          this.sort[header.value],
          this.key_field,
          this.slotWise
        );
      }
    },
    initialSort() {
      const sortObj = {};
      // Always takes one parameter to sort by
      const initialSortParam = this.modifiedHeaders.filter(
        (el) => el.initialSortBy
      );
      let sortByParam = "";
      if (initialSortParam.length) {
        sortByParam = initialSortParam[0].value;
      } else {
        sortByParam = this.modifiedHeaders[0].value;
      }
      this.modifiedHeaders.forEach((header, index) => {
        if (!header.sortable) {
          return;
        }
        if (header.value === sortByParam) {
          sortObj[header.value] = "asc";
          this.activeSort = header.value;
          this.tableData = this.arraySortHandler(
            this.tableData,
            header.value,
            this.sort[header.value],
            this.key_field,
            this.slotWise
          );
        } else {
          sortObj[header.value] = "desc";
        }
      });
      this.sort = sortObj;
    },
    sortStateHandler(param) {
      this.activeSort = param;
      Object.keys(this.sort).forEach((el) => {
        if (el !== param) {
          this.sort[el] = "desc";
        } else {
          if (this.sort[param] === "asc") {
            this.sort[param] = "desc";
          } else {
            this.sort[param] = "asc";
          }
        }
      });
    },
    scrollHandler(className, event) {
      const elm = this.$el.querySelector(`.${className}`);
      elm.scrollLeft = event.target.scrollLeft;
    },
  },
  watch: {
    reduceheight() {
      this.$el.querySelector(".recycler--height").style.height = `calc(100vh - ${this.reduceheight}px)`;
    },
    items() {
      this.tableData = _.cloneDeep(this.items);
      this.initialSort();
      if (this.items.length == 0) {
        const newItem = {};
        this.modifiedHeaders.forEach((el) => {
          newItem[el.value] = "No Data";
        });
        this.tableData.push(newItem);
      }
      this.$el.querySelector(".vue-recycle-scroller").scrollTop = 0;
    },
  },
};
</script>

<style scoped>

.recycler-table {
  display: grid;
}

.recycler-header {
  overflow-x: scroll;
  height: 55px;
  max-height: 55px;
}

.recycler-row {
  border-bottom: 1px solid hsla(0, 0%, 100%, 0.12);
}

.cell {
  padding: 10px;
  text-align: center;
}

.fixed-col {
  background-color: var(--v-primary-base);
  position: sticky;
  left: 0;
}

.recycler-flex {
  display: flex;
}
</style>

<style>
.theme--dark .vue-recycle-scroller__item-view {
  display: flex;
  border-bottom: 1px solid hsla(0, 0%, 100%, 0.12);
}

.theme--light .vue-recycle-scroller__item-view {
  display: flex;
  border-bottom: 1px solid black;
}

.vue-recycle-scroller__item-wrapper {
  overflow: visible;
}

.vue-recycle-scroller.ready.direction-vertical
  .vue-recycle-scroller__item-view {
  width: auto;
}
</style>